/**
 * LAS CASILLAS DE LOS EQUIPOS DEL NEGOCIO (EQUIPOS-PANEL-T1 parte 1, Ares 6-oct por Josse).
 *
 * DE DONDE SALE. La ronda revisa los equipos de cada negocio: de quien son (Nexo o el negocio), si alguien los vio
 * fisicamente y si el negocio firmo el consentimiento de equipos. A (EQUIPOS-API-T1, rama c312e74) importa los 146
 * equipos del link v10 (Firebase) a nexo_private.equipos y el GET de equipos los trae con:
 *   propietario ('nexo'|'negocio'), origen ('firebase:Equipos/...' si vino del link), verificadoFisicamente +
 *   verificadoAt + verificadoPor (del EQUIPO) y consentimientoEquiposFirmado + Fecha + Version (del NEGOCIO, igual en
 *   todas las filas). El PUT no las cambia: en el panel son de SOLO LECTURA.
 * Con la API de antes esas claves no vienen: se dice "sin dato", no "no verificado".
 *
 * Sin DOM: lo prueba scripts/check-casillas-equipo.mjs.
 */

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());
const tiene = (o, k) => o && typeof o === 'object' && (k in o || (k[0].toUpperCase() + k.slice(1)) in o);
const leer = (o, k) => o?.[k] ?? o?.[k[0].toUpperCase() + k.slice(1)];

function fecha(iso) {
  const t = Date.parse(texto(iso));
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Bogota' });
}

/** ¿La respuesta trae las casillas de EQUIPOS-API-T1? (la API de antes no) */
export const traeCasillas = (eq) => tiene(eq, 'verificadoFisicamente');

/** De quien es el equipo: {clave: 'nexo'|'negocio'|'sin_dato', etiqueta} */
export function propietarioDe(eq) {
  const p = texto(leer(eq, 'propietario')).toLowerCase();
  if (p === 'nexo') return { clave: 'nexo', etiqueta: 'De Nexo' };
  if (p === 'negocio') return { clave: 'negocio', etiqueta: 'Del negocio' };
  return { clave: 'sin_dato', etiqueta: 'Propietario sin dato' };
}

/** Verificado fisicamente: {estado: 'si'|'no'|'sin_dato', texto} */
export function verificadoDe(eq) {
  if (!traeCasillas(eq)) return { estado: 'sin_dato', texto: 'Verificacion sin dato' };
  if (leer(eq, 'verificadoFisicamente') !== true) return { estado: 'no', texto: 'Sin verificar fisicamente' };
  const cuando = fecha(leer(eq, 'verificadoAt'));
  const quien = texto(leer(eq, 'verificadoPor'));
  return { estado: 'si', texto: `Verificado${cuando ? ' el ' + cuando : ''}${quien ? ' por ' + quien : ''}` };
}

/** De donde vino el equipo, para leer. '' si no hay origen. */
export function origenDe(eq) {
  const o = texto(leer(eq, 'origen'));
  if (!o) return '';
  return o.toLowerCase().startsWith('firebase:') ? 'Importado del link v10' : o;
}

/** El consentimiento de equipos del NEGOCIO (viene igual en cada fila): {estado: 'firmado'|'no'|'sin_dato', texto} */
export function consentimientoDelNegocio(equipos) {
  const eq = (Array.isArray(equipos) ? equipos : []).find(traeCasillas);
  if (!eq) return { estado: 'sin_dato', texto: 'Consentimiento de equipos: sin dato' };
  if (leer(eq, 'consentimientoEquiposFirmado') !== true) return { estado: 'no', texto: 'Consentimiento de equipos: NO firmado' };
  const cuando = fecha(leer(eq, 'consentimientoEquiposFecha'));
  const version = texto(leer(eq, 'consentimientoEquiposVersion'));
  return { estado: 'firmado', texto: `Consentimiento de equipos firmado${cuando ? ' el ' + cuando : ''}${version ? ' (version ' + version + ')' : ''}` };
}

/** Cuantos verificados, de quien son, y si la API trae las casillas. */
export function resumenDeCasillas(equipos) {
  const lista = Array.isArray(equipos) ? equipos : [];
  const r = { total: lista.length, verificados: 0, deNexo: 0, delNegocio: 0, sinPropietario: 0, conCasillas: lista.some(traeCasillas) };
  for (const eq of lista) {
    if (verificadoDe(eq).estado === 'si') r.verificados += 1;
    const p = propietarioDe(eq).clave;
    if (p === 'nexo') r.deNexo += 1; else if (p === 'negocio') r.delNegocio += 1; else r.sinPropietario += 1;
  }
  return r;
}

// --- MARCAR DESDE EL PANEL (EQUIPOS-PANEL-T1 resto; rutas de A en 1.6.662, PR 424) --------------------------------------

/** El cuerpo del consentimiento, o el error que lo frena: firmar exige la version del texto (la API da 400 sin ella). */
export function cuerpoDeConsentimiento(firmado, version) {
  const v = texto(version);
  if (firmado && !v) return { ok: false, error: 'Escribe la version del texto que firmo el negocio (por ejemplo v1)' };
  return { ok: true, cuerpo: firmado ? { firmado: true, version: v } : { firmado: false } };
}

/** Que decir cuando marcar no pudo. 403 = solo un administrador; 404 sin cuerpo = la ruta aun no esta publicada. */
export function motivoDeMarca(res) {
  if (res?.ok) return { hecho: true };
  const status = Number(res?.code);
  const det = res?.details && typeof res.details === 'object' ? res.details : {};
  const delCuerpo = texto(det.message || det.error);
  if (status === 403) return { error: 'Solo un administrador de Nexo puede marcar estas casillas.' };
  if (status === 404 && !delCuerpo) return { error: 'Marcar aun no esta publicado en la API.' };
  return { error: delCuerpo || texto(res?.error) || 'No se pudo guardar la marca' };
}
